import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import config from '../config';
import { useAuth } from './AuthContext';

// Lives above the routes so an upload keeps running, and can be stopped, from any page.

export interface UploadResults {
  extractedCerts: any[];
  failedFiles: any[];
  duplicates: any[];
  semanticDuplicates: any[];
  olderThanExisting: any[];
  matchFoundCount: number;
  matchFoundRenewals: any[];
  processed: number;
  total: number;
}

export interface UploadBlock {
  title: string;
  message: string;
  showPlanLink: boolean;
}

interface UploadProgress { current: number; total: number; currentFile: string }

interface UploadContextType {
  uploading: boolean;
  progress: UploadProgress | null;
  results: UploadResults | null;
  summaryOpen: boolean;
  block: UploadBlock | null;
  startUpload: (files: File[]) => Promise<void>;
  cancelUpload: () => void;
  openSummary: () => void;
  closeSummary: () => void;
  dismissBlock: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) throw new Error('useUpload must be used within an UploadProvider');
  return context;
};

const formatResetDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long' }) : null;

export const UploadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token: authToken } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [results, setResults] = useState<UploadResults | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [block, setBlock] = useState<UploadBlock | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const runningRef = useRef(false);

  // Closing the tab or reloading does stop an upload, so say so before it happens
  useEffect(() => {
    if (!uploading) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  const cancelUpload = useCallback(() => controllerRef.current?.abort(), []);
  const openSummary = useCallback(() => setSummaryOpen(true), []);
  const closeSummary = useCallback(() => { setSummaryOpen(false); setResults(null); }, []);
  const dismissBlock = useCallback(() => setBlock(null), []);

  const startUpload = useCallback(async (files: File[]) => {
    if (!files.length || runningRef.current) return;
    const token = authToken || localStorage.getItem('auth_token');
    if (!token) return;
    runningRef.current = true;

    try {
      // Check the allowance before anything starts, using fresh numbers
      try {
        const res = await fetch(`${config.apiUrl}/api/payments/status`, { headers: { Authorization: `Bearer ${token}` } });
        const usage = res.ok ? await res.json() : null;
        if (usage && usage.certificate_limit != null) {
          const limit: number = usage.certificate_limit;
          const remaining = Math.max(0, limit - (usage.certificate_count ?? 0));
          const trial = usage.certificate_limit_kind === 'trial';
          const where = trial ? 'on the Trial plan' : 'this month';
          const resets = formatResetDate(usage.certificate_limit_resets_on);
          if (files.length > remaining) {
            let message: string;
            let title = 'Too many certificates selected';
            if (remaining <= 0) {
              title = 'Limit reached';
              message = `You have reached your limit of ${limit} certificates ${where}.`;
            } else if (files.length > limit) {
              message = `You have selected ${files.length} certificates, which is over your limit of ${limit} ${where}.`;
            } else {
              message = `You have selected ${files.length} certificates, but you only have ${remaining} left of your ${limit} ${where}.`;
            }
            message += trial
              ? ' Please select fewer certificates or buy a plan.'
              : ` It resets${resets ? ` on ${resets}` : ' next month'}. Please select fewer certificates.`;
            setBlock({ title, message, showPlanLink: trial });
            return;
          }
        }
      } catch {
        // If the check itself fails, the server still enforces the limit
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      setUploading(true);
      setResults(null);
      setSummaryOpen(false);

      const extractedCerts: any[] = [];
      const failedFiles: any[] = [];
      const duplicates: any[] = [];
      const semanticDuplicates: any[] = [];
      const olderThanExisting: any[] = [];
      const matchFoundRenewals: any[] = [];
      let matchFoundCount = 0;
      let processed = 0;

      for (let i = 0; i < files.length; i++) {
        if (controller.signal.aborted) break;
        const file = files[i];
        setProgress({ current: i + 1, total: files.length, currentFile: file.name });

        const formData = new FormData();
        formData.append('files', file);

        let data: any;
        let status = 0;
        try {
          const resp = await fetch(`${config.apiUrl}/api/bulk-upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
            signal: controller.signal,
          });
          status = resp.status;
          data = await resp.json();
        } catch (err: any) {
          if (controller.signal.aborted) break;
          failedFiles.push({ filename: file.name, error: err?.message || 'Upload failed' });
          processed++;
          continue;
        }

        if (status === 403 && data?.code === 'LIMIT_REACHED') {
          setBlock({ title: 'Limit reached', message: data.error, showPlanLink: data.limit_kind === 'trial' });
          break;
        }
        processed++;

        if (data?.success && data.results?.length > 0) {
          const result = data.results[0];
          if (result.status === 'no_match' && result.certificate_data?.length > 0 && result.file_info) {
            // certificate_data may have multiple entries (one per flat for multi-flat certs)
            result.certificate_data.forEach((cert_data: any, certIndex: number) => {
              extractedCerts.push({ cert_data, file_info: result.file_info, filename: file.name, index: i * 1000 + certIndex });
            });
          } else if (result.status === 'duplicate') {
            duplicates.push({ filename: file.name, ...result.duplicate_info });
          } else if (result.status === 'match_found') {
            matchFoundCount++;
            if (result.pending_renewals?.length) {
              result.pending_renewals.forEach((r: any) => matchFoundRenewals.push({ filename: file.name, ...r }));
            }
          } else if (result.status === 'older_than_existing') {
            olderThanExisting.push({ filename: file.name, ...result.stale_info, cert_data: result.extracted_data, file_info: result.file_info });
          } else if (result.status === 'semantic_duplicate') {
            semanticDuplicates.push({ filename: file.name, ...result.semantic_duplicate_info, cert_data: result.extracted_data, file_info: result.file_info });
          } else if (result.status === 'extraction_failed') {
            failedFiles.push({ filename: file.name, error: result.message, manual_entry_available: true, ai_explanation: result.ai_explanation, file_info: result.file_info });
          } else if (result.status === 'error') {
            failedFiles.push({ filename: file.name, error: result.message });
          }
        } else if (data?.error) {
          failedFiles.push({ filename: file.name, error: data.error });
        }
      }

      setProgress(null);
      // Whatever was read before a stop is still offered for review
      if (processed > 0) {
        setResults({ extractedCerts, failedFiles, duplicates, semanticDuplicates, olderThanExisting, matchFoundCount, matchFoundRenewals, processed, total: files.length });
        if (window.location.pathname === '/dashboard') setSummaryOpen(true);
      }
    } finally {
      controllerRef.current = null;
      runningRef.current = false;
      setUploading(false);
      setProgress(null);
    }
  }, [authToken]);

  return (
    <UploadContext.Provider value={{ uploading, progress, results, summaryOpen, block, startUpload, cancelUpload, openSummary, closeSummary, dismissBlock }}>
      {children}
    </UploadContext.Provider>
  );
};
